// src/data/mock/martyrsPlayers.ts

/**
 * Martyrs Players Data Structure
 *
 * This file contains data for academy players and members who lost their lives.
 *
 * Content is purely humanitarian, honoring their memory.
 *
 * @note Names are sourced from the academy's official memorial list.
 * @note Only verified information is recorded. Sports, ages, photos, and
 *       biographies are intentionally left blank rather than fabricated;
 *       they should be added later only when confirmed with the families.
 * @note All images should be placed in /public/images/martyrs/players/ directory
 */

export interface MartyrPlayer {
  id: string;

  // Basic Information
  name: {
    en: string;
    ar: string;
  };

  // Sport they played
  sport: {
    en: string;
    ar: string;
  };

  // Years Format: "Birth Year - Death Year" (e.g., "2008 - 2023")
  years: string;

  // Age at time of passing
  age?: number;

  // Date of passing in ISO format (YYYY-MM-DD)
  dateOfPassing?: string;

  // Image path (relative to public directory)
  // Recommended: /images/martyrs/players/[id].jpg
  image?: string;

  // Team/Jersey number (if applicable)
  jerseyNumber?: number;

  // Position/Role in their sport
  position?: {
    en: string;
    ar: string;
  };

  // Short biography (2-3 sentences)
  bio?: {
    en: string;
    ar: string;
  };

  // Tribute/memorial message
  tribute?: {
    en: string;
    ar: string;
  };

  // Their dream or aspiration
  dream?: {
    en: string;
    ar: string;
  };

  // A favorite quote or something they often said
  favoriteQuote?: {
    en: string;
    ar: string;
  };

  // Key achievements in their young career
  achievements?: {
    en: string[];
    ar: string[];
  };

  // Personality traits
  traits?: {
    en: string[];
    ar: string[];
  };

  // Years active in academy
  yearsInAcademy?: number;

  // Skill level (for display purposes)
  level?: {
    en: 'Beginner' | 'Intermediate' | 'Advanced' | 'Elite';
    ar: 'مبتدئ' | 'متوسط' | 'متقدم' | 'نخبة';
  };

  // Team they played for (if applicable)
  team?: {
    en: string;
    ar: string;
  };
}

/**
 * Martyrs Players Data
 *
 * Academy players and members (and their families) who were martyred.
 * Names are taken from the academy's memorial list. Additional details
 * (sport, age, photo, biography) are to be added only when verified.
 */
const PLAYER_ROLE = { en: 'Player & Member', ar: 'لاعب وعضو' };
const STAFF_ROLE = { en: 'Staff', ar: 'الطاقم' };

export const martyrsPlayers: MartyrPlayer[] = [
  // ==========================================
  // STAFF / TEAM - الطاقم
  // ==========================================
  {
    id: "mp-001",
    name: { en: "Yousef Al-Heila", ar: "يوسف الحيلة" },
    sport: STAFF_ROLE,
    years: ""
  },
  {
    id: "mp-002",
    name: { en: "Bilal Abu Samaan", ar: "بلال أبو سمعان" },
    sport: STAFF_ROLE,
    years: ""
  },
  {
    id: "mp-003",
    name: { en: "Hani Al-Masdar", ar: "هاني المصدر" },
    sport: STAFF_ROLE,
    years: ""
  },
  {
    id: "mp-004",
    name: { en: "Yousef Salem", ar: "يوسف سالم" },
    sport: STAFF_ROLE,
    years: ""
  },
  {
    id: "mp-005",
    name: { en: "Hala Jarada", ar: "هالة جرادة" },
    sport: STAFF_ROLE,
    years: ""
  },
  {
    id: "mp-006",
    name: { en: "Hassan Al-Sadoudi", ar: "حسن السدودي" },
    sport: STAFF_ROLE,
    years: ""
  },
  {
    id: "mp-007",
    name: { en: "Ismail Shamlakh", ar: "إسماعيل شملخ" },
    sport: STAFF_ROLE,
    years: ""
  },
  {
    id: "mp-008",
    name: { en: "Mohammed Safi", ar: "محمد صافي" },
    sport: STAFF_ROLE,
    years: ""
  },
  {
    id: "mp-009",
    name: { en: "Ahmed Ashour", ar: "أحمد عاشور" },
    sport: STAFF_ROLE,
    years: ""
  },

  // ==========================================
  // PLAYERS & MEMBERS - لاعبين وأعضاء
  // ==========================================
  {
    id: "mp-010",
    name: { en: "Abdul Rahman Abu Yousef", ar: "عبد الرحمن أبو يوسف" },
    sport: PLAYER_ROLE,
    years: ""
  },
  {
    id: "mp-011",
    name: { en: "Laila Al-Atrash", ar: "ليلى الأطرش" },
    sport: PLAYER_ROLE,
    years: ""
  },
  {
    id: "mp-012",
    name: { en: "Yousef Raed Mahdi", ar: "يوسف رائد مهدي" },
    sport: PLAYER_ROLE,
    years: ""
  },
  {
    id: "mp-013",
    name: { en: "Amro Raed Mahdi", ar: "عمرو رائد مهدي" },
    sport: PLAYER_ROLE,
    years: ""
  },
  {
    id: "mp-014",
    name: { en: "Saeed Khurshid", ar: "سعيد خورشيد" },
    sport: PLAYER_ROLE,
    years: ""
  },
  {
    id: "mp-015",
    name: { en: "Mohammed Khurshid", ar: "محمد خورشيد" },
    sport: PLAYER_ROLE,
    years: ""
  },
  {
    id: "mp-016",
    name: { en: "Ahmed Raed Mahdi", ar: "أحمد رائد مهدي" },
    sport: PLAYER_ROLE,
    years: ""
  },
  {
    id: "mp-017",
    name: { en: "Mohammed Raed Mahdi", ar: "محمد رائد مهدي" },
    sport: PLAYER_ROLE,
    years: ""
  },
  {
    id: "mp-018",
    name: { en: "Samira Raed Mahdi", ar: "سميرة رائد مهدي" },
    sport: PLAYER_ROLE,
    years: ""
  },
  {
    id: "mp-019",
    name: { en: "Mira Raed Mahdi", ar: "ميرا رائد مهدي" },
    sport: PLAYER_ROLE,
    years: ""
  },
  {
    id: "mp-020",
    name: { en: "Raed Yousef Mahdi", ar: "رائد يوسف مهدي" },
    sport: PLAYER_ROLE,
    years: ""
  },
  {
    id: "mp-021",
    name: { en: "Abdul Rahman Raed Mahdi", ar: "عبد الرحمن رائد مهدي" },
    sport: PLAYER_ROLE,
    years: ""
  },
  {
    id: "mp-022",
    name: { en: "Wael Bsaiso", ar: "وائل بسيسو" },
    sport: PLAYER_ROLE,
    years: ""
  },
  {
    id: "mp-023",
    name: { en: "Shahd Bsaiso", ar: "شهد بسيسو" },
    sport: PLAYER_ROLE,
    years: ""
  },
  {
    id: "mp-024",
    name: { en: "Maymana Jarada", ar: "ميمنة جرادة" },
    sport: PLAYER_ROLE,
    years: ""
  },
  {
    id: "mp-025",
    name: { en: "Habiba Jarada", ar: "حبيبة جرادة" },
    sport: PLAYER_ROLE,
    years: ""
  },
  {
    id: "mp-026",
    name: { en: "Basel Mahdi", ar: "باسل مهدي" },
    sport: PLAYER_ROLE,
    years: ""
  },
  {
    id: "mp-027",
    name: { en: "Badr Moshtaha", ar: "بدر مشتهى" },
    sport: PLAYER_ROLE,
    years: ""
  },
  {
    id: "mp-028",
    name: { en: "Sham Obaid", ar: "شام عبيد" },
    sport: PLAYER_ROLE,
    years: ""
  }
];

/**
 * Utility Functions
 */

// Get players by sport
export const getPlayersBySport = (sport: string): MartyrPlayer[] => {
  return martyrsPlayers.filter(player =>
    player.sport.en.toLowerCase().includes(sport.toLowerCase())
  );
};

// Get players count
export const getPlayersCount = (): number => {
  return martyrsPlayers.length;
};

// Get players by age group
export const getPlayersByAgeGroup = (minAge: number, maxAge: number): MartyrPlayer[] => {
  return martyrsPlayers.filter(player =>
    player.age && player.age >= minAge && player.age <= maxAge
  );
};

// Get players by level
export const getPlayersByLevel = (level: string): MartyrPlayer[] => {
  return martyrsPlayers.filter(player =>
    player.level?.en.toLowerCase() === level.toLowerCase()
  );
};

// Sort players by age
export const sortPlayersByAge = (ascending: boolean = true): MartyrPlayer[] => {
  return [...martyrsPlayers].sort((a, b) => {
    const ageA = a.age || 0;
    const ageB = b.age || 0;
    return ascending ? ageA - ageB : ageB - ageA;
  });
};

/**
 * Data Validation Helper
 */
export const validatePlayerData = (player: MartyrPlayer): boolean => {
  const required = [
    player.id,
    player.name.en,
    player.name.ar,
    player.sport.en,
    player.sport.ar
  ];

  return required.every(field => field && field.trim() !== '');
};

/**
 * Export Statistics
 */
export const playersStatistics = {
  total: martyrsPlayers.length,
  byAgeGroup: {
    children: martyrsPlayers.filter(p => p.age && p.age < 13).length,  // under 13
    teens: martyrsPlayers.filter(p => p.age && p.age >= 13 && p.age < 18).length,  // 13-17
    youngAdults: martyrsPlayers.filter(p => p.age && p.age >= 18).length,  // 18+
  },
  bySport: martyrsPlayers.reduce((acc, player) => {
    const sport = player.sport.en;
    acc[sport] = (acc[sport] || 0) + 1;
    return acc;
  }, {} as Record<string, number>),
  averageAge: martyrsPlayers.reduce((sum, p) => sum + (p.age || 0), 0) / (martyrsPlayers.filter(p => p.age).length || 1),
  totalYearsInAcademy: martyrsPlayers.reduce((sum, p) => sum + (p.yearsInAcademy || 0), 0)
};
